// EditBookingModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  DatePicker,
  TimePicker,
  Input,
  Button,
  Select,
  InputNumber,
  message,
} from "antd";
import {
  CalendarOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import styles from "./EditBookingModal.module.css";

import {
  fetchBookNowDetail,
  getBookNowDataWithDateObject,
} from "../../../dux/bookNow";
import { isApiPending, isFetchBookNowPending } from "../../../dux/apiStatus";
import {
  parseBookNowApiData,
  mapStartTimeToIndex,
  getConsolidatedTurfNumber,
} from "../../../helpers/bookNowHelper";
import { getCurrentTimeString } from "../../../helpers/dateHelper";
import { updateBookingDetails } from "../../../middleware/admin";

dayjs.extend(customParseFormat);

const { TextArea } = Input;
const { Option } = Select;

// Frontend labels (same naming style as BookNow)
const sportLabelByKey = {
  football: "Football",
  "box-cricket": "Box Cricket",
  "cricket-net": "Cricket Net",
  "pickle-ball": "Pickle Ball",
};

// Field label per sport (like BookNow's fieldMappingAgainstSport)
const fieldLabelForSport = {
  "pickle-ball": "Court",
  football: "Turf",
  "box-cricket": "Turf",
  "cricket-net": "Net",
};

// Field options (turf A/B/C etc.) per sport — labels mirror BookNow UI
const fieldOptionsBySport = {
  football: [
    { key: "turfA", label: "5 a side Turf A" },
    { key: "turfB", label: "5 a side Turf B" },
    { key: "turfC", label: "7 a side Turf C" },
  ],
  "box-cricket": [
    { key: "turfA", label: "5 a side Turf A" },
    { key: "turfB", label: "5 a side Turf B" },
    { key: "turfC", label: "7 a side Turf C" },
  ],
  "pickle-ball": [
    { key: "turfA", label: "Court A" },
    { key: "turfB", label: "Court B" },
  ],
  "cricket-net": [
    { key: "turfA", label: "Net A (3 Players Max)" },
    { key: "turfC", label: "Net A (6 Players Max)" },
    { key: "turfB", label: "Net B (3 Players Max)" },
    { key: "turfD", label: "Net B (6 Players Max)" },
  ],
};

// Parse "08:00 PM", "8:00PM", "08:00", etc. into a Dayjs time (today's date)
function parseStartTimeToDayjs(input) {
  if (!input) return null;
  const s = String(input).trim().toUpperCase();

  const parsed = dayjs(
    s,
    ["HH:mm", "H:mm", "hh:mm A", "h:mm A", "hh:mmA", "h:mmA"],
    true
  );
  if (parsed.isValid()) return parsed;

  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(s);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  const mer = (m[3] || "").toUpperCase();

  if (mer === "AM") {
    if (hour === 12) hour = 0;
  } else if (mer === "PM") {
    if (hour !== 12) hour += 12;
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
}

const EditBookingModal = ({
  visible,
  onClose,
  booking,
  onSuccess,
  refresh,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionType, setActionType] = useState("change_date");

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null); // Dayjs | null
  const [selectedTurf, setSelectedTurf] = useState("");
  const [selectedDuration, setSelectedDuration] = useState(1);

  // Local parsed state (avoid writing to Redux from here)
  const [localSlots, setLocalSlots] = useState({});
  const [localSlotsAvailability, setLocalSlotsAvailability] = useState({});
  const [allSlots, setAllSlots] = useState([]);

  // Pull raw data from store (same selector that BookNow uses)
  const {
    availableSlots,
    pricingConfigs,
    dayMappings,
    datePresenceStatus,
    holidayBookingStatus,
  } = useSelector(getBookNowDataWithDateObject);

  const fetchingBookNowDetail = useSelector(isFetchBookNowPending);
  const isUpdating = useSelector((s) => isApiPending(s, "updateBooking"));

  // Parse/normalize duration to NUMBER of hours
  const durationHours = useMemo(() => {
    if (typeof booking?.duration === "number") return booking.duration || 1;
    const m = String(booking?.duration || "")
      .toLowerCase()
      .match(/\d+/);
    return m ? Math.max(1, parseInt(m[0], 10)) : 1;
  }, [booking?.duration]);

  // ---- Common helpers (match BookNow semantics) ----
  const timeFormat = (timeString) => {
    const [t0, t1] = timeString.split(" - ");
    const a = new Date("1970-01-01T" + t0 + "Z").toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });
    const b = new Date("1970-01-01T" + t1 + "Z").toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });
    return `${a} - ${b}`;
  };

  const getTimeSlot = (time) =>
    `${time} - ${moment(time, ["h:m a", "H:m"])
      .add(1, "hour")
      .format("HH:mm")}`;

  const isTurfPricingAvailable = (turfKey, timeSlot) => {
    if (!timeSlot || !localSlots || !localSlots[turfKey]) return false;
    return localSlots[turfKey].some((slot) => slot.time === timeSlot);
  };

  // ---- Simplified availability logic ----

  // Check if a specific turf is available for the selected time + duration
  const isTurfAvailableForDuration = (turfKey) => {
    if (!selectedTime) return false;

    const startHHmm = selectedTime.format("HH:mm");
    const startIdx = mapStartTimeToIndex[startHHmm];
    if (startIdx == null) return false;

    const arr = localSlotsAvailability?.[turfKey];
    if (!Array.isArray(arr)) return false;

    // Check availability for exactly the selected duration (no extra hour)
    const needed = selectedDuration * 2; // 30-min indices
    for (let i = 0; i < needed; i++) {
      // Check if the index exists in the array (array bounds check)
      if (startIdx + i >= arr.length) return false;
      // Check if the slot is available
      if (!arr[startIdx + i]) return false;
    }
    return isTurfPricingAvailable(turfKey, startHHmm); // pricing must exist too
  };

  // Check if ANY turf is available at a given time slot for the selected duration
  const isAnyTurfAvailableAtTime = (timeString) => {
    const startIdx = mapStartTimeToIndex[timeString];
    if (startIdx == null) return false;

    const sportKey = booking?.sport;
    const turfs = fieldOptionsBySport[sportKey]?.map((t) => t.key) || [];
    const needed = selectedDuration * 2;

    return turfs.some((turfKey) => {
      const arr = localSlotsAvailability?.[turfKey];
      if (!Array.isArray(arr)) return false;

      // Check availability for exactly the selected duration (no extra hour)
      for (let i = 0; i < needed; i++) {
        // Check if the index exists in the array (array bounds check)
        if (startIdx + i >= arr.length) return false;
        // Check if the slot is available
        if (!arr[startIdx + i]) return false;
      }
      return isTurfPricingAvailable(turfKey, timeString);
    });
  };

  // Get the reason why a slot is disabled
  const getSlotDisabledReason = (time /* "HH:mm" */) => {
    const isToday =
      selectedDate &&
      selectedDate.format("DD/MM/YYYY") === dayjs().format("DD/MM/YYYY");
    if (isToday && getCurrentTimeString() > time) {
      return "Past time";
    }

    // Check if this time slot can accommodate the selected duration
    const startIdx = mapStartTimeToIndex[time];
    if (startIdx == null) return "Invalid time";

    // Calculate how many 30-minute slots are needed for the duration
    const neededSlots = selectedDuration * 2;

    // Check if there are enough consecutive slots available from this start time
    const sportKey = booking?.sport;
    const turfs = fieldOptionsBySport[sportKey]?.map((t) => t.key) || [];

    // For 1-hour duration, the slot should be available if it exists
    // For longer durations, check if we have enough consecutive slots
    if (selectedDuration === 1) {
      // 1-hour duration: just check if the slot exists (which it does since we're in this function)
      return null; // slot is available
    } else {
      // For longer durations, check if we have enough consecutive slots
      // We need to find if there are enough slots to cover the duration
      const startMoment = moment(time, "HH:mm");
      const endMoment = startMoment.clone().add(selectedDuration, "hours");
      const endTime = endMoment.format("HH:mm");

      // Check if we have enough consecutive slots for the duration
      // For 2 hours, we need 2 consecutive slots
      const sortedSlots = allSlots.map((s) => s.time).sort();
      const startIndex = sortedSlots.indexOf(time);

      if (startIndex === -1) return "Invalid time";

      // Check if we have enough consecutive slots
      const requiredSlots = selectedDuration;
      if (startIndex + requiredSlots > sortedSlots.length) {
        return `Not enough time for ${selectedDuration} hour(s)`;
      }
    }

    // Check if no turf is available for this time slot with the selected duration
    if (!isAnyTurfAvailableAtTime(time)) {
      return "No turf available";
    }

    // Check if no turf is available for this time slot with the selected duration
    if (!isAnyTurfAvailableAtTime(time)) {
      return "No turf available";
    }

    return null; // slot is available
  };

  // Disable a slot pill if it's in the past today, or no turf can serve it.
  const shouldDisableSlotTime = (time /* "HH:mm" */) => {
    return getSlotDisabledReason(time) !== null;
  };

  // Fetch: identical action to BookNow
  const fetchAvailableSlots = (date, sportKey, location) => {
    if (!date || !sportKey || !location) return;
    const formattedDate = date.format("DD/MM/YYYY");
    dispatch(
      fetchBookNowDetail({
        date: formattedDate,
        sport: sportKey, // booking.sport is already the backend key
        location,
      })
    );
  };

  // Parse raw -> local (no Redux writes)
  useEffect(() => {
    if (availableSlots && pricingConfigs && selectedDate) {
      try {
        const parsed = parseBookNowApiData(
          availableSlots,
          pricingConfigs,
          selectedDate.toDate(),
          dayMappings,
          datePresenceStatus
        );

        if (holidayBookingStatus?.booking_allowed === false) {
          parsed.bookingAllowed = false;
        }

        setLocalSlots(parsed.slots || {});
        setLocalSlotsAvailability(parsed.slotsAvailability || {});
      } catch {
        setLocalSlots({});
        setLocalSlotsAvailability({});
      }
    } else {
      setLocalSlots({});
      setLocalSlotsAvailability({});
    }
  }, [
    availableSlots,
    pricingConfigs,
    selectedDate,
    dayMappings,
    datePresenceStatus,
    holidayBookingStatus,
  ]);

  // Combine turfs -> dedupe -> sort (same as BookNow)
  useEffect(() => {
    const combined = [];
    if (localSlots?.turfA?.length) combined.push(...localSlots.turfA);
    if (localSlots?.turfB?.length) combined.push(...localSlots.turfB);
    if (localSlots?.turfC?.length) combined.push(...localSlots.turfC);

    const uniqueSorted = combined
      .filter(
        (slot, i, self) => i === self.findIndex((s) => s.time === slot.time)
      )
      .sort((a, b) => a.time.localeCompare(b.time));

    setAllSlots(uniqueSorted);
  }, [localSlots]);

  // Init when modal opens
  useEffect(() => {
    if (!visible || !booking) return;

    let parsedDate = null;
    let parsedTime = null;

    if (booking.playingDate) {
      parsedDate = dayjs(booking.playingDate);
      if (!parsedDate.isValid())
        parsedDate = dayjs(booking.playingDate, "DD/MM/YYYY");
      if (!parsedDate.isValid())
        parsedDate = dayjs(booking.playingDate, "YYYY-MM-DD");
      if (!parsedDate.isValid()) parsedDate = dayjs();
    }

    if (booking.startTime) {
      parsedTime =
        parseStartTimeToDayjs(booking.startTime) || dayjs().hour(6).minute(0);
    }

    form.setFieldsValue({
      action_type: "change_date",
      new_date: parsedDate,
      new_time: parsedTime,
      new_duration: durationHours,
      notes: "",
    });

    setSelectedDate(parsedDate);
    setSelectedTime(parsedTime);
    setSelectedDuration(durationHours);
    setActionType("change_date");
    setSelectedTurf("");

    if (parsedDate && booking.sport && booking.location) {
      fetchAvailableSlots(parsedDate, booking.sport, booking.location);
    }
  }, [visible, booking, form, durationHours]);

  const handleDateChange = (date) => {
    if (!date) {
      setSelectedDate(null);
      setAllSlots([]);
      setSelectedTurf("");
      return;
    }
    setSelectedDate(date);
    setAllSlots([]);
    setSelectedTurf("");
    if (booking?.sport && booking?.location) {
      fetchAvailableSlots(date, booking.sport, booking.location);
    }
  };

  // AntD gives Dayjs | null
  const handleTimeChange = (time /* Dayjs|null */) => {
    if (!time || !time.isValid()) {
      setSelectedTime(null);
      form.setFieldsValue({ new_time: null });
      setSelectedTurf("");
      return;
    }
    setSelectedTime(time);
    form.setFieldsValue({ new_time: time });
    setSelectedTurf(""); // reset field when time changes
  };

  const handleDurationChange = (duration) => {
    const d = Math.max(1, Math.min(4, Number(duration || 1)));
    setSelectedDuration(d);
    form.setFieldsValue({ new_duration: d });
    setSelectedTurf("");
  };

  const handleActionTypeChange = (value) => {
    setActionType(value);
    if (value === "cancel") {
      form.setFieldsValue({
        new_date: undefined,
        new_time: undefined,
        new_duration: undefined,
      });
    } else {
      form.setFieldsValue({
        new_date: selectedDate || dayjs(),
        new_time: selectedTime || dayjs().hour(6).minute(0),
        new_duration: selectedDuration || 1,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const payload = {
        booking_id: booking.bookingId,
        action_type: actionType,
        notes: values.notes,
      };

      if (actionType === "change_date") {
        payload.new_date = values.new_date.format("DD/MM/YYYY");
        payload.new_time = values.new_time.format("HH:mm");
        payload.new_duration = values.new_duration;

        if (selectedTurf) {
          payload.new_turf_number = Number(
            getConsolidatedTurfNumber(
              booking.sport, // backend key (football, box-cricket, etc.)
              selectedTurf, // turfA/turfB/...
              booking.location // location slug
            )
          );
        }
      }

      dispatch(updateBookingDetails(payload, refresh));

      message.success(
        actionType === "cancel"
          ? "Booking cancellation request sent successfully!"
          : "Booking update request sent successfully!"
      );
      onSuccess?.();
      onClose?.();
    } catch {
      // validation errors already surfaced by antd
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose?.();
  };

  const renderFieldChooser = () => {
    const sportKey = booking?.sport;
    const fieldLabel = fieldLabelForSport[sportKey];
    const options = fieldOptionsBySport[sportKey] || [];

    if (!fieldLabel || options.length === 0) return null;
    if (!selectedTime) return null; // only show after time is selected

    return (
      <div className={styles.availableSlotsSection} style={{ marginTop: 16 }}>
        <h4>
          Select {fieldLabel}:{` `}
          <small style={{ fontWeight: 400, color: "#888" }}>
            {sportLabelByKey[sportKey] || sportKey}
          </small>
        </h4>

        <div
          className={styles.slotsGrid}
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {options.map(({ key, label }) => {
            const disabled = !isTurfAvailableForDuration(key);
            const isSelected = selectedTurf === key;
            return (
              <Button
                key={key}
                type={isSelected ? "primary" : "default"}
                onClick={() => {
                  if (!disabled) setSelectedTurf(key);
                }}
                disabled={disabled}
                style={{
                  height: 44,
                  justifyContent: "flex-start",
                  opacity: disabled ? 0.6 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
                block
              >
                {label}
                {disabled && (
                  <span
                    style={{ marginLeft: 8, fontSize: "12px", color: "#999" }}
                  >
                    (Unavailable)
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFormContent = () => {
    if (actionType === "cancel") {
      return (
        <div className={styles.cancelSection}>
          <div className={styles.warningMessage}>
            <CloseOutlined className={styles.warningIcon} />
            <span>This action will cancel the booking permanently.</span>
          </div>
          <Form.Item
            name="notes"
            label="Cancellation Reason"
            rules={[{ required: true, message: "Please provide a reason" }]}
          >
            <TextArea
              rows={4}
              placeholder="Please provide a reason for cancelling this booking..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </div>
      );
    }

    return (
      <div className={styles.changeDateSection}>
        <div className={styles.dateTimeSection}>
          <Form.Item
            name="new_date"
            label="New Playing Date"
            rules={[{ required: true, message: "Please select a new date" }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="Select new date"
              onChange={handleDateChange}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              className={styles.datePicker}
            />
          </Form.Item>

          <Form.Item
            name="new_time"
            label="New Start Time"
            rules={[{ required: true, message: "Please select a new time" }]}
          >
            <TimePicker
              use12Hours
              format="hh:mm A"
              placeholder="Select new time"
              onChange={handleTimeChange}
              minuteStep={60}
              className={styles.timePicker}
            />
          </Form.Item>

          <Form.Item
            name="new_duration"
            label="New Duration"
            rules={[{ required: true, message: "Please enter new duration" }]}
          >
            <InputNumber
              min={1}
              max={4}
              onChange={handleDurationChange}
              className={styles.durationInput}
            />
          </Form.Item>
        </div>

        {selectedDate && (
          <div className={styles.availableSlotsSection}>
            <h4>Available Slots for {selectedDate.format("DD/MM/YYYY")}</h4>

            {fetchingBookNowDetail ? (
              <div className={styles.loadingSlots}>
                <div className={styles.spinner}></div>
                <span>Loading available slots...</span>
              </div>
            ) : allSlots && allSlots.length > 0 ? (
              <div className={styles.slotsGrid}>
                {(allSlots || []).map(({ time, cost }) => {
                  const disabled = shouldDisableSlotTime(time);
                  const disabledReason = getSlotDisabledReason(time);
                  const isSelected =
                    selectedTime && selectedTime.format("HH:mm") === time;
                  return (
                    <div
                      key={time}
                      className={`${styles.slotItem} ${
                        isSelected ? styles.selectedSlot : ""
                      }`}
                      style={
                        disabled
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : undefined
                      }
                      onClick={() => {
                        if (disabled) return;
                        const [h, m] = time.split(":").map(Number);
                        const newTime = dayjs()
                          .hour(h)
                          .minute(m)
                          .second(0)
                          .millisecond(0);
                        form.setFieldsValue({ new_time: newTime });
                        setSelectedTime(newTime);
                        setSelectedTurf(""); // reset field selection when time changes
                      }}
                      aria-disabled={disabled}
                      title={disabled ? disabledReason : undefined}
                    >
                      <div className={styles.slotTime}>
                        {timeFormat(getTimeSlot(time))}
                      </div>
                      {typeof cost === "number" && cost > 0 && (
                        <div className={styles.slotCost}>₹{cost}</div>
                      )}
                      {disabled && (
                        <div
                          className={styles.slotDisabledReason}
                          style={{
                            fontSize: "10px",
                            color: "#999",
                            marginTop: "2px",
                          }}
                        >
                          {disabledReason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.noSlotsMessage}>
                No slots available for this date. Please select a different
                date.
              </div>
            )}
          </div>
        )}

        {/* Turf/Net/Court chooser (renders only after a time is selected) */}
        {renderFieldChooser()}

        <Form.Item name="notes" label="Change Reason (Optional)">
          <TextArea
            rows={3}
            placeholder="Please provide a reason for changing the booking date..."
            maxLength={300}
            showCount
            className={styles.notesInput}
          />
        </Form.Item>
      </div>
    );
  };

  // Main component render
  if (!booking) return null;

  return (
    <Modal
      title={
        <div className={styles.modalTitle}>
          <EditOutlined className={styles.titleIcon} />
          <span>Edit Booking - {booking.bookingId}</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          disabled={isSubmitting}
          className={styles.modalCancelButton}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          onClick={handleSubmit}
          loading={isSubmitting || isUpdating}
          danger={actionType === "cancel"}
          className={
            actionType === "cancel" ? styles.cancelButton : styles.updateButton
          }
        >
          {actionType === "cancel" ? "Cancel Booking" : "Update Booking"}
        </Button>,
      ]}
      width={700}
      destroyOnHidden
    >
      <div className={styles.bookingInfo}>
        <h4>Current Booking Details</h4>
        <div className={styles.bookingDetails}>
          <div className={styles.detailItem}>
            <strong>Customer:</strong> {booking.name}
          </div>
          <div className={styles.detailItem}>
            <strong>Sport:</strong>{" "}
            {sportLabelByKey[booking.sport] || booking.sport}
          </div>
          <div className={styles.detailItem}>
            <strong>{fieldLabelForSport[booking.sport]}:</strong>{" "}
            {
              fieldOptionsBySport[booking.sport]?.find(
                (field) => field.key === `turf${booking.turf}`
              )?.label
            }
          </div>
          <div className={styles.detailItem}>
            <strong>Current Date:</strong> {booking.playingDate}
          </div>
          <div className={styles.detailItem}>
            <strong>Current Time:</strong> {booking.startTime}
          </div>
          <div className={styles.detailItem}>
            <strong>Duration:</strong> {durationHours} hour(s)
          </div>
        </div>
      </div>

      <Form form={form} layout="vertical" className={styles.editForm}>
        <Form.Item
          name="action_type"
          label="What would you like to do?"
          rules={[{ required: true, message: "Please select an action" }]}
        >
          <Select onChange={handleActionTypeChange}>
            <Option value="change_date">
              <CalendarOutlined /> Change Booking Date & Time
            </Option>
            <Option value="cancel">
              <CloseOutlined /> Cancel Booking
            </Option>
          </Select>
        </Form.Item>

        {renderFormContent()}
      </Form>
    </Modal>
  );
};

export default EditBookingModal;
